import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Chip,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  CalendarToday as CalendarTodayIcon
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import axios from 'axios';
import AppointmentService from '../../services/AppointmentService';

const AppointmentList = () => {
  const navigate = useNavigate();
  
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState(null);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  useEffect(() => {
    const fetchAppointments = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await AppointmentService.getAllAppointments();
        const fetchedAppointments = response.data.map(app => ({
          id: app.appointmentId,
          patientName: app.patient ? app.patient.nickname : 'N/A',
          patientId: app.patientId,
          date: app.appointmentDate.split('T')[0],
          time: app.startTime,
          type: app.appointmentType || 'N/A',
          physician: app.doctor ? `${app.doctor.firstName} ${app.doctor.lastName}` : 'N/A',
          status: app.status,
          notes: app.description || app.notes || 'No notes',
          numberOfPersons: app.numberOfPersons || 1,
          // Add other fields as needed for display
        }));
        const toDateTime = (a) => {
          const d = (a.date || '').toString().split('T')[0];
          const t = (a.time || '').toString();
          const ts = `${d}T${t || '00:00'}`;
          return new Date(ts);
        };
        const sorted = [...fetchedAppointments].sort((a, b) => toDateTime(b) - toDateTime(a));
        setAppointments(sorted);
        setFilteredAppointments(sorted);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching appointments:', error);
        setError('Failed to load appointments. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchAppointments();
  }, []);
  
  useEffect(() => {
    // Filter appointments based on search term, status, and date
    let filtered = appointments;
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(appointment => 
        appointment.patientName.toLowerCase().includes(term) ||
        appointment.physician.toLowerCase().includes(term) ||
        appointment.type.toLowerCase().includes(term)
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(appointment => appointment.status === statusFilter);
    }
    
    if (dateFilter) {
      const filterDate = new Date(dateFilter);
      filtered = filtered.filter(appointment => {
        // Backend returns 'appointmentDate' but frontend expects 'date'
        const dateValue = appointment.appointmentDate || appointment.date;
        const appointmentDate = new Date(dateValue);
        return (
          appointmentDate.getDate() === filterDate.getDate() &&
          appointmentDate.getMonth() === filterDate.getMonth() &&
          appointmentDate.getFullYear() === filterDate.getFullYear()
        );
      });
    }
    
    const toDateTime = (a) => {
      const base = (a.appointmentDate || a.date || '').toString().split('T')[0];
      const t = (a.startTime || a.time || '').toString();
      const ts = `${base}T${t || '00:00'}`;
      return new Date(ts);
    };
    const sorted = [...filtered].sort((a, b) => toDateTime(b) - toDateTime(a));
    setFilteredAppointments(sorted);
    setPage(0); // Reset to first page when filtering
  }, [searchTerm, statusFilter, dateFilter, appointments]);
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };
  
  const clearSearch = () => {
    setSearchTerm('');
  };
  
  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };
  
  const handleDateFilterChange = (date) => {
    setDateFilter(date);
  };
  
  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setDateFilter(null);
  };
  
  const handleViewAppointment = (appointmentId) => {
    navigate(`/appointments/${appointmentId}`);
  };
  
  const handleEditAppointment = (appointmentId) => {
    navigate(`/appointments/${appointmentId}/edit`);
  };
  
  const handleDeleteAppointment = async (appointmentId) => {
    // In a real application, you would call your API to delete the appointment
    // For now, we'll just filter the appointment from the local state
    if (window.confirm('Are you sure you want to delete this appointment?')) {
      try {
        // Mock API call
        await new Promise(resolve => setTimeout(resolve, 500));
        
        setAppointments(appointments.filter(appointment => appointment.id !== appointmentId));
      } catch (error) {
        console.error('Error deleting appointment:', error);
        alert('Failed to delete appointment. Please try again.');
      }
    }
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'Scheduled':
        return 'primary';
      case 'Completed':
        return 'success';
      case 'Cancelled':
        return 'error';
      default:
        return 'default';
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" variant="h6">{error}</Typography>
        <Button variant="contained" sx={{ mt: 2 }} onClick={() => window.location.reload()}>
          Retry
        </Button>
      </Box>
    );
  }
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Appointments
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/appointments/new')}
        >
          New Appointment
        </Button>
      </Box>
      
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                placeholder="Search appointments..."
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={clearSearch}>
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel id="status-filter-label">Status</InputLabel>
                <Select
                  labelId="status-filter-label"
                  id="status-filter"
                  value={statusFilter}
                  label="Status"
                  onChange={handleStatusFilterChange}
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="Scheduled">Scheduled</MenuItem>
                  <MenuItem value="Completed">Completed</MenuItem>
                  <MenuItem value="Cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Filter by Date"
                  value={dateFilter}
                  onChange={handleDateFilterChange}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              <Button
                variant="outlined"
                onClick={clearFilters}
                startIcon={<ClearIcon />}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 440 }}>
          <Table stickyHeader aria-label="appointments table">
            <TableHead>
              <TableRow>
                <TableCell>Patient</TableCell>
                <TableCell>Date & Time</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Physician</TableCell>
                <TableCell>Persons</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAppointments
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((appointment) => (
                  <TableRow hover key={appointment.id}>
                    <TableCell component="th" scope="row">
                      {appointment.patientName}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(appointment.appointmentDate || appointment.date).toLocaleDateString()}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {appointment.startTime || appointment.time}
                      </Typography>
                    </TableCell>
                    <TableCell>{appointment.type}</TableCell>
                    <TableCell>{appointment.physician}</TableCell>
                    <TableCell>{appointment.numberOfPersons}</TableCell>
                    <TableCell>
                      <Chip 
                        label={appointment.status}
                        color={getStatusColor(appointment.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View">
                        <IconButton
                          size="small"
                          onClick={() => handleViewAppointment(appointment.id)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleEditAppointment(appointment.id)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteAppointment(appointment.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              {filteredAppointments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No appointments found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredAppointments.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
};

export default AppointmentList;